import type { PushSubscriptionPayload } from './api.js';

export type PushUnavailableReason =
  | 'server'
  | 'insecure'
  | 'serviceWorker'
  | 'pushManager'
  | 'notifications'
  | 'vapidKey';

export interface BrowserPushState {
  supported: boolean;
  reason: PushUnavailableReason | null;
  permission: NotificationPermission | 'unsupported';
  endpoint: string | null;
}

export async function readBrowserPushState(): Promise<BrowserPushState> {
  const capability = getPushCapability();
  if (!capability.supported) {
    return {
      supported: false,
      reason: capability.reason,
      permission: notificationPermission(),
      endpoint: null,
    };
  }

  const subscription = await getExistingSubscription();
  return {
    supported: true,
    reason: null,
    permission: Notification.permission,
    endpoint: subscription?.endpoint ?? null,
  };
}

export async function registerBrowserPush(): Promise<PushSubscriptionPayload> {
  const capability = getPushCapability();
  if (!capability.supported) {
    throw new Error(capability.reason);
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('permission-denied');
    }
  }

  await navigator.serviceWorker.register('/notification-sw.js');
  const activeRegistration = await navigator.serviceWorker.ready;
  const existing = await activeRegistration.pushManager.getSubscription();
  if (existing) return subscriptionToPayload(existing);

  const subscription = await activeRegistration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToArrayBuffer(vapidPublicKey()),
  });
  return subscriptionToPayload(subscription);
}

export async function unregisterBrowserPush(): Promise<string | null> {
  const subscription = await getExistingSubscription();
  if (!subscription) return null;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
}

function getPushCapability():
  | { supported: true; reason: null }
  | { supported: false; reason: PushUnavailableReason } {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { supported: false, reason: 'server' };
  }
  if (!window.isSecureContext) return { supported: false, reason: 'insecure' };
  if (!('serviceWorker' in navigator)) {
    return { supported: false, reason: 'serviceWorker' };
  }
  if (!('PushManager' in window)) return { supported: false, reason: 'pushManager' };
  if (!('Notification' in window)) {
    return { supported: false, reason: 'notifications' };
  }
  if (vapidPublicKey().length === 0) {
    return { supported: false, reason: 'vapidKey' };
  }
  return { supported: true, reason: null };
}

function vapidPublicKey(): string {
  return typeof import.meta.env.VITE_NOTIFICATIONS_PUSH_VAPID_PUBLIC_KEY === 'string'
    ? import.meta.env.VITE_NOTIFICATIONS_PUSH_VAPID_PUBLIC_KEY.trim()
    : '';
}

function notificationPermission(): BrowserPushState['permission'] {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return null;
  }

  const registration = await navigator.serviceWorker.getRegistration('/notification-sw.js');
  return (await registration?.pushManager.getSubscription()) ?? null;
}

function subscriptionToPayload(subscription: PushSubscription): PushSubscriptionPayload {
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
      auth: arrayBufferToBase64Url(subscription.getKey('auth')),
    },
  };
}

function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) throw new Error('missing-subscription-key');
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

function base64UrlToArrayBuffer(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output.buffer;
}
